import { z } from 'zod'

/**
 * ワークスペース名の規則（Issue #126）。全体で一意で、運営画面・通知メールでワークスペースを見分けるのに使う。
 *
 * API の入力検証・新規登録時の自動付与・画面の入力チェックで同じ規則を使う。
 * 既存のワークスペースにはマイグレーションの SQL で名前を付けている。
 * 規則を変えるときは packages/db/prisma/migrations/*_tenant_name_unique の CHECK 制約も確認すること。
 */

export const WORKSPACE_NAME_MIN_LENGTH = 3
export const WORKSPACE_NAME_MAX_LENGTH = 30

/** 自動で付けた名前が規則に合わないとき（短すぎる・予約語など）や、元にする名前が無いときに使う名前 */
const FALLBACK_WORKSPACE_NAME = 'workspace'

/**
 * 運営や公式と見分けがつかない名前。他人に紛らわしい表示をされるのを防ぐ。
 * `workspace` は自動付与の逃げ先なので入れない。
 */
const RESERVED_WORKSPACE_NAMES: ReadonlySet<string> = new Set([
  'admin',
  'administrator',
  'root',
  'system',
  'support',
  'help',
  'staff',
  'official',
  'operator',
  'owner',
  'security',
  'abuse',
  'billing',
  'contact',
  'info',
  'mail',
  'noreply',
  'no-reply',
  'api',
  'www',
  'terafile',
  'terafile-bin',
  'me',
  'settings',
  'login',
  'logout',
  'signup',
  'null',
  'undefined',
])

export type WorkspaceNameProblem =
  | 'too_short'
  | 'too_long'
  | 'invalid_chars'
  | 'edge_hyphen'
  | 'double_hyphen'
  | 'reserved'

/** 画面と API のエラーで共通に使う文言（docs/ui-copy-guide.md に従う） */
export const WORKSPACE_NAME_PROBLEM_MESSAGES: Record<WorkspaceNameProblem, string> = {
  too_short: `ワークスペース名は${WORKSPACE_NAME_MIN_LENGTH}文字以上で入力してください`,
  too_long: `ワークスペース名は${WORKSPACE_NAME_MAX_LENGTH}文字以内で入力してください`,
  invalid_chars: '半角の英小文字・数字・ハイフン（-）だけが使えます',
  edge_hyphen: 'ハイフン（-）は先頭と末尾には使えません',
  double_hyphen: 'ハイフン（-）を2つ続けて使うことはできません',
  reserved: 'このワークスペース名は使えません。別の名前を入力してください',
}

/**
 * 規則に合わなければ理由を、合えば null を返す。入力は小文字にそろえた後の値を想定。
 *
 * 使えない文字を長さより先に見る。「田中」は2文字だが、伝えるべきは
 * 「短い」ではなく「その文字は使えない」なので。
 */
export function findWorkspaceNameProblem(name: string): WorkspaceNameProblem | null {
  if (name.length > 0 && !/^[a-z0-9-]+$/.test(name)) return 'invalid_chars'
  if (name.length < WORKSPACE_NAME_MIN_LENGTH) return 'too_short'
  if (name.length > WORKSPACE_NAME_MAX_LENGTH) return 'too_long'
  if (name.startsWith('-') || name.endsWith('-')) return 'edge_hyphen'
  if (name.includes('--')) return 'double_hyphen'
  if (RESERVED_WORKSPACE_NAMES.has(name)) return 'reserved'
  return null
}

/**
 * 表記ゆれをそろえる。全角の英数字・記号を半角に（NFKC）、前後の空白を除き、小文字にする。
 * `ＴａｎａｋａＳｔｕｄｉｏ` と `tanakastudio` を同じ名前として扱い、一意の判定もこの後の値で行う。
 */
export function normalizeWorkspaceName(input: string): string {
  return input.normalize('NFKC').trim().toLowerCase()
}

/** 入力されたワークスペース名。表記ゆれをそろえてから規則を確かめる */
export const WorkspaceNameSchema = z
  .string()
  .transform(normalizeWorkspaceName)
  .superRefine((name, ctx) => {
    const problem = findWorkspaceNameProblem(name)
    if (problem) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: WORKSPACE_NAME_PROBLEM_MESSAGES[problem],
      })
    }
  })

/**
 * メールアドレスから自動で付けるワークスペース名の候補。重複は考慮しない（pickAvailableWorkspaceName で解く）。
 *
 * 1. @ より前を小文字にする
 * 2. 英小文字・数字以外の連続を 1 つのハイフンにまとめる
 * 3. 先頭と末尾のハイフンを除き、上限の長さで切る（切った結果の末尾のハイフンも除く）
 * 4. 空なら `workspace`、短すぎれば末尾に `-workspace`、予約語なら `workspace`
 */
export function deriveWorkspaceNameBase(email: string): string {
  const local = (email.split('@')[0] ?? '').toLowerCase()
  const slug = trimHyphens(local.replace(/[^a-z0-9]+/g, '-'))
  const clipped = trimHyphens(slug.slice(0, WORKSPACE_NAME_MAX_LENGTH))

  if (clipped.length === 0) return FALLBACK_WORKSPACE_NAME
  const padded =
    clipped.length < WORKSPACE_NAME_MIN_LENGTH ? `${clipped}-${FALLBACK_WORKSPACE_NAME}` : clipped
  return RESERVED_WORKSPACE_NAMES.has(padded) ? FALLBACK_WORKSPACE_NAME : padded
}

/**
 * 候補が使われていれば末尾に 2, 3… を付けて空きを探す（`tanaka` → `tanaka2`）。
 * 数字を付けても上限を超えないよう、元の名前の末尾を削る。
 */
export function pickAvailableWorkspaceName(
  base: string,
  isTaken: (name: string) => boolean,
): string {
  if (!isTaken(base)) return base
  for (let n = 2; ; n++) {
    const suffix = String(n)
    const candidate = `${base.slice(0, WORKSPACE_NAME_MAX_LENGTH - suffix.length)}${suffix}`
    if (!isTaken(candidate)) return candidate
  }
}

function trimHyphens(value: string): string {
  return value.replace(/^-+|-+$/g, '')
}
